import { navigateChannel } from '../../shell/Sidebar';
import {
    batchSetRecentChannelPin,
    removeInvitationsByChannelId,
    setActionStatus,
    setActiveConversationChannel,
    setChannelInfo,
    setChannelUnreadCount,
    setMultipleChannelsInfo,
    setPinnedStatus,
    setProgress,
    setRecentChannels,
    setRecentChannelUnreadRecords,
    updateDeleteMessages,
    updateInvitation,
    updateInvitationCount,
    updateMediaStatus,
    upsertRecentChannelItem,
} from '../../slice';
import { clearChannelsConversationExceptActive } from '../../slice/channelConversationSlice';
import { Store } from '../../store';
import logger from '../../Helpers/logger';
import {
    handleAddMemberInChannel,
    handleChannelCreatedOrAdded,
    handleChannelIncomingMessage,
    handleChannelMemberRemoved,
    handleChannelRemoved,
    handleDeleteChannel,
    handleLeaveMemberInChannel,
    handleMemberJoinChannel,
    handleMissingMessagePublished,
    handleReactionEvent,
    handleRoleChangedEvent,
    updateChannelActiveStatus,
} from './channelEventHandler';
import { NOTIFICATION_TYPES, sendChannelEventNotification } from './notificationsService';
import { refreshChannelMessages } from './refreshChannelMessages';

export const handleIncomingChannelEvents = eventPayload => {
    try {
        logger.debug('eventPayload ==>', eventPayload);
        if (!eventPayload || typeof eventPayload !== 'object') return;

        const { event, body } = eventPayload;
        const data = body?.data;
        const initiatedBy = body?.initiatedBy;

        if (!event || !data) return logger.debug('No event or data found', eventPayload);

        switch (event) {
            case 'MESSAGE_PUBLISHED':
                handleChannelIncomingMessage(data);
                break;
            case 'MISSING_MESSAGE_PUBLISHED':
                handleMissingMessagePublished(data);
                break;
            case 'MESSAGE_REACTION':
                handleReactionEvent(data);
                break;
            case 'CHANNEL_CREATED':
            case 'ADDED_TO_CHANNEL':
                if (data.channel && data.participants) {
                    handleChannelCreatedOrAdded({ data, event });
                }
                break;

            case 'CHANNEL_MEMBER_ADDED':
                handleAddMemberInChannel(data);
                break;
            case 'JOINED_CHANNEL':
            case 'CHANNEL_MEMBER_JOINED':
                handleMemberJoinChannel(data, event);
                break;

            case 'MESSAGE_DELETED': {
                const { channelId, deleteForAll, messageId } = data;
                // Delete the message
                Store.dispatch(
                    updateDeleteMessages({
                        channelId,
                        messageIds: [messageId],
                        isDeleteForAll: deleteForAll,
                    }),
                );

                break;
            }

            case 'CHANNEL_MEMBER_LEFT':
            case 'LEFT_CHANNEL': {
                handleLeaveMemberInChannel(data);
                break;
            }

            case 'CHANNEL_MEMBER_REMOVED':
            case 'REMOVED_FROM_CHANNEL': {
                handleChannelMemberRemoved(data);
                break;
            }

            case 'ROLE_CHANGED': {
                handleRoleChangedEvent(data);
                break;
            }

            case 'CHANNEL_DELETED': {
                data.initiatedBy = initiatedBy;
                handleDeleteChannel(data);
                Store.dispatch(removeInvitationsByChannelId({ channelId: data.channelId }));
                break;
            }

            case 'CHANNEL_REMOVED': {
                data.initiatedBy = initiatedBy;
                handleChannelRemoved(data);
                break;
            }

            case 'CHANNEL_ACTIVATED':
            case 'CHANNEL_DEACTIVATED': {
                updateChannelActiveStatus(data);
                break;
            }
            case 'CHAT_MUTE': {
                const { action: isMuted = 0, channelIds = [] } = data;
                Store.dispatch(setActionStatus({ channelIds, isMuted }));
                break;
            }

            case 'CHAT_PIN': {
                const { action = 0, chats = [] } = data;
                if (chats.length > 0) {
                    Store.dispatch(setPinnedStatus({ chats, action }));
                    Store.dispatch(batchSetRecentChannelPin({ chats, action }));
                }
                break;
            }

            case 'CHANNEL_UPDATED':
            case 'CHANNEL_SETTINGS_UPDATED': {
                if (data.channel.pinnedAt > 0) {
                    Store.dispatch(upsertRecentChannelItem(data.channel));
                }
                Store.dispatch(setChannelInfo(data.channel));
                break;
            }

            case 'ACTIVITY_CREATED': {
                const activity = data.activity;
                // Send notification for new join request
                const channelDetails = activity.channelDetails || {};
                const initiatedBy = activity.initiatedBy || {};
                const channelId = activity.channelId || data.channelId || channelDetails.channelId;

                sendChannelEventNotification(
                    NOTIFICATION_TYPES.NEW_JOIN_REQUEST,
                    {
                        channelName: channelDetails.name || activity.channelDetails?.name,
                        channelId: channelId,
                        channel: channelDetails,
                        username: initiatedBy.nickName || initiatedBy.name || initiatedBy.userId || 'Someone',
                        initiatedBy: initiatedBy,
                    },
                    {
                        channelId: channelId,
                        onClick: _e => {
                            // Navigate to invitations section when notification is clicked
                            if (navigateChannel?.current) {
                                navigateChannel.current.toInvitations();
                            }
                        },
                    },
                );
                Store.dispatch(updateInvitation(activity));
                Store.dispatch(
                    updateInvitationCount({
                        status: activity?.status,
                    }),
                );
                break;
            }

            case 'CHANNEL_APPROVED':
            case 'CHANNEL_DECLINED': {
                const activity = data.activity;
                const currentUserId = Store.getState().vCardData?.data?.userId;
                const initiatedByUserId = activity?.initiatedBy?.userId;

                if (initiatedByUserId === currentUserId) {
                    const channelDetails = activity.channelDetails || {};
                    const channelId = activity.channelId || data.channelId || channelDetails.channelId;
                    const notificationType =
                        event === 'CHANNEL_APPROVED'
                            ? NOTIFICATION_TYPES.REQUEST_ACCEPTED
                            : NOTIFICATION_TYPES.REQUEST_DECLINED;
                    if (event === 'CHANNEL_APPROVED') {
                        // Completely refresh channel messages data when approved
                        refreshChannelMessages(channelId);
                    }
                    sendChannelEventNotification(
                        notificationType,
                        {
                            channelName: channelDetails.name,
                            channelId: channelId,
                            channel: channelDetails,
                        },
                        {
                            channelId: channelId,
                            ...(event === 'CHANNEL_APPROVED' && {
                                onClick: _e => {
                                    // Navigate to the channel if approved
                                    if (navigateChannel?.current) {
                                        navigateChannel.current.toRecentChannels(channelId);
                                        Store.dispatch(setActiveConversationChannel({ channelId: channelId }));
                                    }
                                },
                            }),
                        },
                    );
                }
                Store.dispatch(updateInvitation(data.activity));
                Store.dispatch(
                    updateInvitationCount({
                        status: data.activity.status,
                    }),
                );
                break;
            }

            case 'MEDIA_PROGRESS': {
                Store.dispatch(setProgress(data));
                break;
            }

            case 'MEDIA_CANCEL': {
                if (data.isUpload) {
                    Store.dispatch(
                        updateMediaStatus({
                            channelId: data.channelId,
                            messageId: data.messageId,
                            is_uploading: 3,
                        }),
                    );
                }
                break;
            }

            case 'CHAT_READ': {
                const { channelId = '', unreadCount = 0, messageId = '' } = data;

                // Get the previous unread count for this channel
                const previousUnreadCount = Store.getState().channelInfoData.channelInfo[channelId]?.unreadCount || 0;

                // Only decrement global unread counter if channel transitions from unread to read
                if (channelId && previousUnreadCount > 0 && unreadCount === 0) {
                    const unreadRecords = Store.getState().recentChannelListData.unreadRecords;
                    Store.dispatch(setRecentChannelUnreadRecords(unreadRecords - 1 > 0 ? unreadRecords - 1 : 0));
                }

                Store.dispatch(
                    setChannelUnreadCount({
                        channelId,
                        unreadCount: unreadCount,
                        messageId: messageId,
                    }),
                );

                break;
            }

            case 'RECENT_CHANNELS_FETCHED': {
                const channels = data?.channels || [];
                const unreadRecords = data?.unreadRecords || 0;
                // Get active channel ID
                const activeChannel = Store.getState().channelNavData?.activeConversationChannel;
                const activeChannelId = activeChannel?.channelId || null;

                // Reset conversation data for all channels except the active one
                if (activeChannelId) {
                    Store.dispatch(
                        clearChannelsConversationExceptActive({
                            activeChannelId,
                        }),
                    );
                }

                // Always dispatch even if empty to reset the state
                Store.dispatch(setMultipleChannelsInfo(channels));

                Store.dispatch(
                    setRecentChannels({
                        channels,
                        unreadRecords,
                    }),
                );
                break;
            }

            default:
                logger.debug('channel event not matched', eventPayload);
                break;
        }
    } catch (error) {
        logger.error('Failed to handle incoming channel event:', error);
    }
};
